import React from 'react';
import { useTranslation } from 'react-i18next';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, IconButton, Stack } from '@mui/material';
import { ControlWrapper, InputSelect } from '../../../../../../components';
import { RangeHeader } from '../../../../../../components/range-header';
import { AggregateVectorParameterTypeInternal } from '../../../../../../data-access/gql-types/graphql';
import { ChannelType } from '../../../../types/channel';
import { LoadingState } from '../analysis/components/chart/loading-state';
import { EmptyPrices } from '../common/components/empty-prices/empty-prices';
import { useMeterDetailsContext } from '../context';
import { ComparatorChart } from './components/chart/chart';
import { ComparatorGridRow } from './components/grid-row/grid-row';
import { useChartComparator } from './hooks/use-chart-comparator';

type ChartComparatorProps = {
  channel: ChannelType;
};

export const Comparator: React.FC<ChartComparatorProps> = ({ channel }) => {
  const { t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');
  const { comparatorTariffs, setComparatorPage, comparatorPage, comparatorParam, setComparatorParam } =
    useMeterDetailsContext();
  const {
    operatorOptions,
    loading,
    chartData,
    range,
    getTariffOptions,
    compareCharts,
    handleAddRow,
    handleChange,
    handleRemoveRow,
    emptyTariffs,
  } = useChartComparator(channel);

  return (
    <div className="meter-comparator">
      <ControlWrapper className="control-wrapper--column  control-wrapper--reverse p-l-24 p-r-24 p-b-16 ">
        <Box>
          <ComparatorGridRow>
            <InputSelect
              label=""
              value={comparatorParam}
              options={[
                {
                  label: t(`energyMeasurements.types.summary.${AggregateVectorParameterTypeInternal.Price}`),
                  value: AggregateVectorParameterTypeInternal.Price,
                },
                {
                  label: t(`energyMeasurements.types.summary.${AggregateVectorParameterTypeInternal.Cost}`),
                  value: AggregateVectorParameterTypeInternal.Cost,
                },
                {
                  label: t(
                    `energyMeasurements.types.summary.${AggregateVectorParameterTypeInternal.VectorBalancedCost}`,
                  ),
                  value: AggregateVectorParameterTypeInternal.VectorBalancedCost,
                },
              ]}
              onChange={setComparatorParam}
              reverse
            />
          </ComparatorGridRow>

          {comparatorTariffs.map((row, idx) => (
            <ComparatorGridRow key={idx}>
              <InputSelect
                label="Operator"
                value={row.operator}
                options={operatorOptions}
                onChange={(val) => handleChange(idx, 'operator', val)}
                reverse
              />
              <InputSelect
                label="Tariff"
                value={row.tariff}
                options={row.operator ? getTariffOptions(row.operator, idx) : []}
                onChange={(val) => handleChange(idx, 'tariff', val)}
                disabled={!row.operator}
                reverse
              />
              <Stack direction="row" alignItems="center">
                <IconButton
                  onClick={() => handleRemoveRow(idx)}
                  sx={{
                    color: 'var(--color-red)',
                    visibility: idx > 0 ? 'visible' : 'hidden',
                    padding: '6px',
                    height: '32px',
                    width: '32px',
                  }}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Stack>
            </ComparatorGridRow>
          ))}
          <ComparatorGridRow containerClassName="m-b-24">
            <button
              onClick={handleAddRow}
              className="button button--secondary"
              disabled={comparatorTariffs.length > 11}
            >
              {tc('buttons.add')}
            </button>
            <button onClick={compareCharts} disabled={loading} className="button">
              {tc('buttons.save')}
            </button>
          </ComparatorGridRow>
        </Box>

        {emptyTariffs ? <EmptyPrices channelId={channel.id} /> : null}

        <RangeHeader
          prevAvailable={comparatorPage > -7}
          nextAvailable={comparatorPage < 0}
          range={range}
          setPage={setComparatorPage}
        />
        {!loading ? chartData.length ? <ComparatorChart chartData={chartData} /> : null : <LoadingState />}
      </ControlWrapper>
    </div>
  );
};
